import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { getCookieConsent, CookiePreferences } from "@/utils/cookieConsent";

interface CookieConsentContextType {
  consentState: CookiePreferences | null;
  requestConsent: () => void;
  hasConsent: (type: 'analytics') => boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [consentState, setConsentState] = useState<CookiePreferences | null>(() => getCookieConsent());
  const [shouldShowBanner, setShouldShowBanner] = useState(false);

  // Demander le consentement si nécessaire
  const requestConsent = useCallback(() => {
    const consent = getCookieConsent();
    
    if (!consent) {
      // Pas de consentement enregistré, afficher le bandeau
      setShouldShowBanner(true);
      // Dispatch event pour afficher le bandeau
      window.dispatchEvent(new CustomEvent('requestCookieConsent'));
    }
  }, []);

  // Vérifier si l'utilisateur a donné son consentement pour un type spécifique
  const hasConsent = useCallback((type: 'analytics') => {
    const consent = getCookieConsent();
    return consent?.[type] === true;
  }, []);

  // Écouter les changements de consentement
  const handleConsentUpdate = useCallback(() => {
    const newConsent = getCookieConsent();
    setConsentState(newConsent);
    setShouldShowBanner(false);
  }, []);

  // Ajouter les event listeners au montage
  useState(() => {
    window.addEventListener('cookieConsentUpdated', handleConsentUpdate);
    window.addEventListener('cookieConsentCleared', handleConsentUpdate);
    
    return () => {
      window.removeEventListener('cookieConsentUpdated', handleConsentUpdate);
      window.removeEventListener('cookieConsentCleared', handleConsentUpdate);
    };
  });

  return (
    <CookieConsentContext.Provider value={{ consentState, requestConsent, hasConsent }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return context;
};
